import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { readStoredConsent, acceptAll, rejectAll, updateConsent } from "@/lib/consent";
import { NavLink } from "react-router-dom";

// External helper to open the preferences modal from elsewhere (Footer)
let externalOpen = null;
export const openCookiePrefs = () => externalOpen && externalOpen();

export default function CookieBanner() {
  const { lang, t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [modal, setModal] = useState(false);
  const [prefs, setPrefs] = useState({ functional: true, analytics: false, ads: false });

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) setVisible(true);
    else {
      setPrefs({
        functional: stored.functionality_storage === "granted",
        analytics: stored.analytics_storage === "granted",
        ads: stored.ad_storage === "granted",
      });
    }
  }, []);

  useEffect(() => {
    externalOpen = () => setModal(true);
    return () => { externalOpen = null; };
  }, []);

  const handleAccept = useCallback(() => {
    acceptAll();
    setVisible(false);
    setModal(false);
  }, []);
  const handleReject = useCallback(() => {
    rejectAll();
    setVisible(false);
    setModal(false);
  }, []);
  const handleSave = useCallback(() => {
    updateConsent(prefs);
    setVisible(false);
    setModal(false);
  }, [prefs]);

  const cookiePolicyHref = lang === "ar" ? "/ar/cookies" : "/en/cookies";

  return (
    <>
      <AnimatePresence>
        {visible && !modal && (
          <motion.div
            key="banner"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-4 inset-x-4 sm:inset-x-6 lg:inset-x-8 z-[60]"
            style={{ wordBreak: "break-word" }}
            data-testid="cookie-banner"
          >
            <div className="mx-auto max-w-6xl bg-[#121316] text-[#F8F7F4] rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.55)] border border-[#2A2B30] overflow-hidden">
              <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-7">
                  <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#C27835]">
                    {t("cookies.bannerTitle")}
                  </p>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#D8D9DE]">
                    {t("cookies.bannerBody")}{" "}
                    <NavLink to={cookiePolicyHref} className="underline underline-offset-4 hover:text-[#C27835]" data-testid="cookie-banner-policy-link">
                      {t("cookies.policyLink")}
                    </NavLink>
                  </p>
                </div>
                <div className="lg:col-span-5 flex flex-col sm:flex-row lg:justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setModal(true)}
                    data-testid="cookie-manage-button"
                    className="px-5 py-3 rounded-full text-sm border border-[#3A3B40] hover:border-[#C27835] hover:text-[#C27835] transition-colors duration-200"
                  >
                    {t("cookies.manage")}
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    data-testid="cookie-reject-button"
                    className="px-5 py-3 rounded-full text-sm border border-[#3A3B40] hover:bg-[#1A1B1F] transition-colors duration-200"
                  >
                    {t("cookies.rejectAll")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAccept}
                    data-testid="cookie-accept-all-button"
                    className="px-5 py-3 rounded-full text-sm bg-[#C27835] hover:bg-[#A66324] text-[#121316] font-medium transition-colors duration-200"
                  >
                    {t("cookies.acceptAll")}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
            data-testid="cookie-modal"
          >
            <div className="absolute inset-0 bg-[#121316]/60 backdrop-blur-sm" onClick={() => setModal(false)} />
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-2xl bg-[#F8F7F4] text-[#121316] rounded-2xl border border-[#E5E3DC] overflow-hidden"
              style={{ wordBreak: "break-word" }}
            >
              <div className="p-6 sm:p-8">
                <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]">{t("cookies.modalTitle")}</p>
                <h3 className="mt-2 font-serif text-3xl sm:text-4xl leading-tight">{t("cookies.modalTitle")}</h3>
                <p className="mt-3 text-sm text-[#52535A] leading-relaxed">{t("cookies.modalIntro")}</p>

                <div className="mt-6 divide-y divide-[#E5E3DC]">
                  <PrefRow
                    title={t("cookies.necessaryTitle")}
                    body={t("cookies.necessaryBody")}
                    checked
                    disabled
                    testId="pref-necessary"
                  />
                  <PrefRow
                    title={t("cookies.functionalTitle")}
                    body={t("cookies.functionalBody")}
                    checked={prefs.functional}
                    onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                    testId="pref-functional"
                  />
                  <PrefRow
                    title={t("cookies.analyticsTitle")}
                    body={t("cookies.analyticsBody")}
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                    testId="pref-analytics"
                  />
                  <PrefRow
                    title={t("cookies.adsTitle")}
                    body={t("cookies.adsBody")}
                    checked={prefs.ads}
                    onChange={(v) => setPrefs((p) => ({ ...p, ads: v }))}
                    testId="pref-ads"
                  />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button type="button" onClick={handleReject} data-testid="cookie-modal-reject" className="px-5 py-3 rounded-full text-sm border border-[#E5E3DC] hover:border-[#121316] transition-colors duration-200">
                    {t("cookies.rejectAll")}
                  </button>
                  <button type="button" onClick={handleSave} data-testid="cookie-modal-save" className="px-5 py-3 rounded-full text-sm border border-[#121316] hover:bg-[#121316] hover:text-[#F8F7F4] transition-colors duration-200">
                    {t("cookies.save")}
                  </button>
                  <button type="button" onClick={handleAccept} data-testid="cookie-modal-accept" className="px-5 py-3 rounded-full text-sm bg-[#C27835] hover:bg-[#A66324] text-[#121316] font-medium transition-colors duration-200">
                    {t("cookies.acceptAll")}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function PrefRow({ title, body, checked, onChange, disabled, testId }) {
  return (
    <div className="py-4 flex items-start justify-between gap-6">
      <div>
        <p className="font-serif text-lg">{title}</p>
        <p className="text-sm text-[#52535A] leading-relaxed mt-1">{body}</p>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
        aria-pressed={checked}
        data-testid={testId}
        className={`shrink-0 relative w-12 h-7 rounded-full border transition-colors duration-200 ${
          checked ? "bg-[#C27835] border-[#C27835]" : "bg-white border-[#E5E3DC]"
        } ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform duration-200 shadow ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}
